<button type="button" class="pick-button" [class.active]="picking" (click)="togglePicking()">
  <ion-icon name="color-wand-outline"></ion-icon>
  <span>{{ (picking ? "commons.themeConfiguration.inspector.stop" : "commons.themeConfiguration.inspector.start") | translate }}</span>
</button>

<p class="pick-hint" *ngIf="picking">{{ "commons.themeConfiguration.inspector.hint" | translate }}</p>

<!-- Inspector for whatever was picked, at the top so it is the first thing seen. -->
<section class="inspector" *ngIf="selection">
  <header>
    <span class="element-label">{{ selection.label }}</span>
    <ion-icon name="close-outline" (click)="clearSelection()"></ion-icon>
  </header>

  <div class="role" *ngFor="let group of selection.byRole">
    <h6>{{ ("commons.themeConfiguration.inspector.role." + group.role) | translate }}</h6>

    <div class="property" *ngFor="let item of group.items">
      <span class="property-name">{{ ("commons.themeConfiguration.colors." + item.definition.key) | translate }}</span>

      <div class="mode-row" *ngFor="let mode of modes">
        <span class="mode-label">{{ ("commons.themeConfiguration.inspector." + mode) | translate }}</span>

        <!-- A native select cannot show a colour next to each name, so the list is our own. The
             trigger carries the swatch, which is why there is none beside it. -->
        <div class="theme-dropdown">
          <button type="button" class="dropdown-trigger"
            (click)="toggleDropdown(item.definition.key, mode)">
            <span class="option-swatch" [style.background]="sourceOf(item.definition.key, mode).value"></span>
            <span class="option-name">{{ optionLabel(item.definition.key, mode) | translate }}</span>
            <ion-icon name="chevron-down-outline"></ion-icon>
          </button>

          <ul class="dropdown-list" *ngIf="dropdownIsOpen(item.definition.key, mode)">
            <li *ngFor="let name of themeNames">
              <button type="button" [class.selected]="name === selectedOption(item.definition.key, mode)"
                (click)="pickOption(item.definition.key, mode, name)">
                <span class="option-swatch" [style.background]="optionSwatch(name, mode)"></span>
                <span class="option-name">{{ ("commons.themeConfiguration.theme." + name) | translate }}</span>
                <span class="option-value">{{ optionSwatch(name, mode) }}</span>
              </button>
            </li>
            <li>
              <button type="button" [class.selected]="selectedOption(item.definition.key, mode) === custom"
                (click)="pickOption(item.definition.key, mode, custom)">
                <span class="option-swatch custom-swatch"></span>
                <span class="option-name">{{ "commons.themeConfiguration.inspector.custom" | translate }}</span>
              </button>
            </li>
          </ul>
        </div>

        <ion-icon name="refresh-outline" class="reset" *ngIf="canReset(item.definition.key, mode)"
          [title]="'commons.themeConfiguration.inspector.reset' | translate"
          (click)="resetElement(item.definition.key, mode)"></ion-icon>
      </div>

      <p class="mirrors-light" *ngIf="mirrorsLight(item.definition.key, 'dark')">
        {{ "commons.themeConfiguration.inspector.mirrorsLight" | translate }}
      </p>

      <color-chrome *ngIf="isOpen('element', item.definition.key, openPicker?.mode)"
        [color]="sourceOf(item.definition.key, openPicker.mode).value" [disableAlpha]="true"
        (onChangeComplete)="setElementColor(item.definition.key, openPicker.mode, $event.color.hex)"></color-chrome>
    </div>
  </div>
</section>

<!-- Puts the page into a state that only happens when something fails, so the messages it
     shows can be branded at all. -->
<section class="states">
  <h6 class="section-title" (click)="statesOpen = !statesOpen">
    <ion-icon [name]="statesOpen ? 'chevron-down-outline' : 'chevron-forward-outline'"></ion-icon>
    {{ "commons.themeConfiguration.sections.states" | translate }}
  </h6>

  <div class="state-list" *ngIf="statesOpen">
    <!-- Only what this screen implements, so nothing here can be clicked to no effect. -->
    <button type="button" *ngFor="let state of states" [class.selected]="isStateOn(state.key)"
      (click)="toggleState(state.key)">
      {{ ("commons.themeConfiguration.states." + state.key) | translate }}
    </button>

    <p class="state-empty" *ngIf="!states.length">
      {{ "commons.themeConfiguration.states.none" | translate }}
    </p>
  </div>
</section>

<!-- The theme itself: a handful of colours everything else points at. -->
<section class="theme">
  <h6 class="section-title" (click)="themeOpen = !themeOpen">
    <ion-icon [name]="themeOpen ? 'chevron-down-outline' : 'chevron-forward-outline'"></ion-icon>
    {{ "commons.themeConfiguration.sections.theme" | translate }}
  </h6>

  <div class="theme-colors" *ngIf="themeOpen">
    <!-- The handful a brand actually decides comes first; the rest is behind a disclosure, so the
         panel does not open with twelve colours demanding an opinion. -->
    <span class="color-group">{{ "commons.themeConfiguration.theme.brandGroup" | translate }}</span>

    <div class="theme-color" *ngFor="let color of brandColors">
      <span class="property-name">{{ ("commons.themeConfiguration.theme." + color.name) | translate }}</span>
      <span class="property-hint">{{ ("commons.themeConfiguration.theme." + color.name + "Description") | translate }}</span>

      <div class="mode-row" *ngFor="let mode of modes">
        <span class="mode-label">{{ ("commons.themeConfiguration.inspector." + mode) | translate }}</span>

        <button type="button" class="swatch" [style.background]="themeValue(color.name, mode)"
          (click)="toggle('theme', color.name, mode)" [title]="themeValue(color.name, mode)"></button>

        <span class="value">{{ themeValue(color.name, mode) }}</span>

        <span class="inherited" *ngIf="themeIsInherited(color.name, mode)">
          {{ "commons.themeConfiguration.inspector.usesDefault" | translate }}
        </span>

        <ion-icon name="refresh-outline" class="reset" *ngIf="!themeIsInherited(color.name, mode) && mode === 'dark'"
          [title]="'commons.themeConfiguration.inspector.reset' | translate"
          (click)="clearThemeDark(color.name)"></ion-icon>
      </div>

      <color-chrome *ngIf="isOpen('theme', color.name, openPicker?.mode)"
        [color]="themeValue(color.name, openPicker.mode)" [disableAlpha]="true"
        (onChangeComplete)="setThemeColor(color.name, openPicker.mode, $event.color.hex)"></color-chrome>
    </div>

    <button type="button" class="disclosure" (click)="supportingOpen = !supportingOpen">
      <ion-icon [name]="supportingOpen ? 'chevron-down-outline' : 'chevron-forward-outline'"></ion-icon>
      {{ "commons.themeConfiguration.theme.supportingGroup" | translate }}
    </button>

    <ng-container *ngIf="supportingOpen">
      <span class="group-hint">{{ "commons.themeConfiguration.theme.supportingHint" | translate }}</span>

      <div class="theme-color" *ngFor="let color of supportingColors">
        <span class="property-name">{{ ("commons.themeConfiguration.theme." + color.name) | translate }}</span>
        <span class="property-hint">{{ ("commons.themeConfiguration.theme." + color.name + "Description") | translate }}</span>

        <div class="mode-row" *ngFor="let mode of modes">
          <span class="mode-label">{{ ("commons.themeConfiguration.inspector." + mode) | translate }}</span>

          <button type="button" class="swatch" [style.background]="themeValue(color.name, mode)"
            (click)="toggle('theme', color.name, mode)" [title]="themeValue(color.name, mode)"></button>

          <span class="value">{{ themeValue(color.name, mode) }}</span>

          <span class="inherited" *ngIf="themeIsInherited(color.name, mode)">
            {{ "commons.themeConfiguration.inspector.usesDefault" | translate }}
          </span>

          <ion-icon name="refresh-outline" class="reset"
            *ngIf="!themeIsInherited(color.name, mode) && mode === 'dark'"
            [title]="'commons.themeConfiguration.inspector.reset' | translate"
            (click)="clearThemeDark(color.name)"></ion-icon>
        </div>

        <color-chrome *ngIf="isOpen('theme', color.name, openPicker?.mode)"
          [color]="themeValue(color.name, openPicker.mode)" [disableAlpha]="true"
          (onChangeComplete)="setThemeColor(color.name, openPicker.mode, $event.color.hex)"></color-chrome>
      </div>
    </ng-container>
  </div>
</section>
